import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useResolvedMediaUrl } from '../hooks/useResolvedMediaUrl';
import AmbientBackdrop from './AmbientBackdrop';
import WatchTracker from './WatchTracker';

type MediaType = 'image' | 'video';

interface MediaBoxProps {
  src: string;
  type: MediaType;
  alt?: string;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  contain?: boolean; // if true, object-fit: contain; else cover
  maxHeight?: number | string; // e.g., '60vh' or 400
  // Optional intrinsic size hints to avoid CLS and set aspect-ratio immediately
  widthHint?: number;
  heightHint?: number;
  // If true, clamp aspect ratio into social-friendly bounds (4:5 to 16:9) using object-fit cover
  clampSocial?: boolean;
  // When set, real playback time counts towards the content creator's watch hours.
  watchContent?: { type: 'post' | 'pulse' | 'testimony'; id: string };
  poster?: string | null;
}

const MediaBox: React.FC<MediaBoxProps> = ({
  src,
  type,
  alt = '',
  controls = true,
  autoPlay = false,
  muted = false,
  loop = false,
  contain = false,
  maxHeight = '60vh',
  widthHint,
  heightHint,
  clampSocial = false,
  watchContent,
  poster,
}) => {
  const resolvedSrc = useResolvedMediaUrl(src);
  const resolvedPoster = useResolvedMediaUrl(type === 'video' ? poster : '');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(
    widthHint && heightHint ? { w: widthHint, h: heightHint } : null
  );
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setLoading(true);
    setError(false);
  }, [resolvedSrc, type]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const w = img.naturalWidth || 1;
    const h = img.naturalHeight || 1;
    setNaturalSize({ w, h });
    setLoading(false);
  };

  const handleVideoLoad = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const vid = e.currentTarget;
    const w = vid.videoWidth || 1;
    const h = vid.videoHeight || 1;
    setNaturalSize({ w, h });
    setLoading(false);
  };

  const handleVideoError = () => {
    setError(true);
    setLoading(false);
  };

  const handleImageError = () => {
    setError(true);
    setLoading(false);
  };

  const toggleVideoFullscreen = () => {
    if (videoRef.current) {
      if (!document.fullscreenElement) {
        videoRef.current.requestFullscreen?.().catch(err => console.warn('Fullscreen failed:', err));
      } else {
        document.exitFullscreen?.();
      }
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  // Prefer a taller viewport for images by default
  const resolvedMaxH = useMemo(() => (typeof maxHeight === 'number' ? `${maxHeight}px` : (type === 'image' ? '80vh' : maxHeight)), [maxHeight, type]);

  // Aspect ratio calculation and optional clamping to social-friendly bounds
  const aspect = useMemo(() => {
    const w = naturalSize?.w || widthHint || 1;
    const h = naturalSize?.h || heightHint || 1;
    let ratio = w / h;
    if (clampSocial) {
      const min = 4 / 5;   // 0.8 (portrait max)
      const max = 16 / 9;  // 1.777... (landscape max)
      ratio = Math.min(Math.max(ratio, min), max);
    }
    return ratio;
  }, [naturalSize, widthHint, heightHint, clampSocial]);

  const aspectStyle = useMemo(() => ({ aspectRatio: `${aspect} / 1` }), [aspect]);

  // Container uses CSS aspect-ratio to take the shape of media; media fills container with object-fit
  const containerStyle: React.CSSProperties = {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: type === 'video' ? '#072B2C' : '#f9f9f9',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    position: 'relative',
    maxHeight: resolvedMaxH,
    ...aspectStyle,
    cursor: type === 'image' ? 'zoom-in' : 'default',
  };

  const shouldContain = contain || type === 'image';

  const mediaStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: shouldContain ? 'contain' : 'cover',
    display: loading ? 'none' : 'block',
  };

  const fullscreenOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    cursor: 'zoom-out',
  };

  const fullscreenImageStyle: React.CSSProperties = {
    maxWidth: '95vw',
    maxHeight: '95vh',
    objectFit: 'contain',
    borderRadius: 8,
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: 20,
    right: 20,
    backgroundColor: 'rgba(255,255,255,0.9)',
    border: 'none',
    borderRadius: 8,
    padding: '10px 16px',
    fontSize: 20,
    cursor: 'pointer',
    fontWeight: 'bold',
    color: '#333',
    zIndex: 10000,
  };

  const skeletonOverlay: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: 0,
  };

  const errorStateStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '40px 20px',
    color: type === 'video' ? '#fff' : '#666',
    fontSize: '14px',
  };

  const videoOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: loading ? 'rgba(0,0,0,0.3)' : 'transparent',
    pointerEvents: loading ? 'none' : 'auto',
  };

  const videoControlsOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 12,
    right: 12,
    display: 'flex',
    gap: 8,
    zIndex: 10,
  };

  const controlButtonStyle: React.CSSProperties = {
    background: 'rgba(0,0,0,0.6)',
    border: 'none',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background 0.2s',
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={containerStyle} onClick={() => type === 'image' && setIsFullscreen(true)}>
        {type === 'image' ? (
          <>
            {loading && (
              <div className="skeleton" style={skeletonOverlay} />
            )}
            {error ? (
              <div style={errorStateStyle}>
                <span style={{ fontSize: '32px' }}>🖼️</span>
                <span>Failed to load image</span>
              </div>
            ) : (
              <img 
                src={resolvedSrc || ''} 
                alt={alt} 
                style={mediaStyle} 
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}
          </>
        ) : (
          <>
            <AmbientBackdrop />
            {watchContent?.id && <WatchTracker key={watchContent.id} contentType={watchContent.type} contentId={watchContent.id} />}
            <video
              ref={videoRef}
              src={resolvedSrc || ''}
              poster={resolvedPoster || undefined}
              style={mediaStyle}
              controls={controls}
              autoPlay={autoPlay}
              muted={muted}
              loop={loop}
              playsInline
              preload="metadata"
              onLoadedMetadata={handleVideoLoad}
              onError={handleVideoError}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            {loading && (
              <div style={videoOverlayStyle}>
                <div className="skeleton" style={{ ...skeletonOverlay, background: 'transparent' }} />
              </div>
            )}
            {error && (
              <div style={errorStateStyle}>
                <span style={{ fontSize: '48px' }}>🎬</span>
                <span>Video unavailable</span>
                <span style={{ fontSize: '12px', opacity: 0.7 }}>Failed to load video content</span>
              </div>
            )}
            {!loading && !error && !controls && (
              <div style={videoControlsOverlayStyle}>
                <button 
                  style={controlButtonStyle}
                  onClick={togglePlayPause}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
                >
                  {isPlaying ? '⏸' : '▶'}
                </button>
                <button 
                  style={controlButtonStyle}
                  onClick={toggleVideoFullscreen}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
                >
                  ⛶
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isFullscreen && type === 'image' && (
        <div style={fullscreenOverlayStyle} onClick={() => setIsFullscreen(false)}>
          <button style={closeButtonStyle} onClick={() => setIsFullscreen(false)}>✕</button>
          <img src={src} alt={alt} style={fullscreenImageStyle} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

export default MediaBox;
